import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { LoadScript, GoogleMap, Polyline, Marker } from '@react-google-maps/api';
import {
  Box, Flex, HStack, Input, Button, VStack, Text, Spinner,
  Alert, AlertIcon, AlertTitle, AlertDescription, Badge,
} from '@chakra-ui/react';
import { getGoogleMapApiKey } from '../../Utilities/Apikeys';
import { getTechnicianLogsAndLocations, getTechnicians } from '../../Utilities/networks';
import { formatLogTime } from '../../Utilities/dateFormat';
import PageShell from '../ui/PageShell';
import DataCard from '../ui/DataCard';

const containerStyle = { width: '100%', height: '400px' };

export default function TechnicianDetail() {
  const { id } = useParams();
  const location = useLocation();
  const apiKey = getGoogleMapApiKey();
  const [from, setFrom] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [to, setTo] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [logs, setLogs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [technician, setTechnician] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const technicianId = String(id).replace('tech_', '');

  useEffect(() => {
    const fromNavigation = location.state?.technician;
    if (fromNavigation?.name) {
      setTechnician((prev) => prev || {
        id: technicianId,
        name: fromNavigation.name,
        phone: fromNavigation.phone,
        area: fromNavigation.area,
        status: fromNavigation.status,
        technician_status: fromNavigation.technician_status,
      });
    }

    const loadTechnician = async () => {
      try {
        const response = await getTechnicians();
        const list = response.data?.data || [];
        const match = list.find((t) => String(t.id) === String(technicianId));
        if (match) setTechnician(match);
      } catch {
        /* name stays unavailable */
      }
    };
    loadTechnician();
  }, [technicianId, location.state]);

  // formatLogTime imported from Utilities/dateFormat

  // Helper function to clean and validate location data
  const cleanLocationData = (data) => {
    return data.map(item => ({
      ...item,
      latitude: parseFloat(item.latitude) || null,
      longitude: parseFloat(item.longitude) || null
    }));
  };

  // Helper function to clean and validate log data
  const cleanLogData = (data) => {
    return data.map(item => ({
      ...item,
      location_lat: item.location_lat !== null && item.location_lat !== undefined ? parseFloat(item.location_lat) : null,
      location_lng: item.location_lng !== null && item.location_lng !== undefined ? parseFloat(item.location_lng) : null
    }));
  };

  const fetchData = useCallback(async () => {
    if (!from || !to) return;

    setLoading(true);
    setError(null);

    try {
      const response = await getTechnicianLogsAndLocations(
        parseInt(technicianId),
        from,
        to
      );

      // axios transformResponse should have already parsed the JSON
      // response.data should already be a parsed object
      let responseData = response.data;
      // Final fallback: if response is still a string, try to parse it
      if (typeof responseData === 'string') {
        try {
          responseData = JSON.parse(responseData);
        } catch (forceParseError) {
          setError('Invalid JSON response from server');
          setLoading(false);
          return;
        }
      }

      // Validate the response structure
      if (!responseData) {
        setError('No data received from server');
        return;
      }

      // Additional validation to ensure we have valid data structure
      if (typeof responseData !== 'object') {
        setError('Invalid response format from server');
        return;
      }

      if (responseData && responseData.status === 'success') {
        // Clean and validate the data
        const cleanedLogs = cleanLogData(responseData.logs || []);
        const cleanedLocations = cleanLocationData(responseData.locations || []);

        // Validate cleaned data
        const logsValid = cleanedLogs.every(log =>
          log.location_lat === null ||
          (typeof log.location_lat === 'number' && !isNaN(log.location_lat))
        );
        const locationsValid = cleanedLocations.every(loc =>
          typeof loc.latitude === 'number' && !isNaN(loc.latitude) &&
          typeof loc.longitude === 'number' && !isNaN(loc.longitude)
        );

        if (!logsValid || !locationsValid) {
          // Data validation failed, some values may still be invalid
        }

        setLogs(cleanedLogs);
        setLocations(cleanedLocations);
        setError(null); // Clear any previous errors
      } else {
        const errorMessage = responseData?.message || 'Failed to fetch data';
        setError(errorMessage);
      }
    } catch (err) {
      if (err.response) {
        // Server responded with error status
        let errorMessage = 'Server error occurred';
        if (typeof err.response.data === 'string') {
          // Try to extract error message from string response
          try {
            const errorData = JSON.parse(err.response.data);
            errorMessage = errorData?.message || `Server error: ${err.response.status}`;
          } catch {
            errorMessage = err.response.data || `Server error: ${err.response.status}`;
          }
        } else if (typeof err.response.data === 'object' && err.response.data?.message) {
          errorMessage = err.response.data.message;
        } else {
          errorMessage = `Server error: ${err.response.status}`;
        }

        setError(errorMessage);
      } else if (err.request) {
        // Network error
        setError('Network error. Please check your connection and try again.');
      } else {
        // Other error
        setError('Failed to fetch data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [technicianId, from, to]);

  useEffect(() => {
    // Test API connectivity and JSON parsing FIRST
    
    fetchData();
  }, [fetchData]);

  // Convert locations to path format for polyline
  const path = useMemo(() =>
    locations.map(loc => ({
      lat: parseFloat(loc.latitude),
      lng: parseFloat(loc.longitude)
    })), [locations]
  );

  const start = useMemo(() =>
    path.length > 0 ? path[0] : { lat: 0, lng: 0 }, [path]
  );

  const end = useMemo(() =>
    path.length > 0 ? path[path.length - 1] : { lat: 0, lng: 0 }, [path]
  );

  const center = useMemo(() => {
    if (path.length === 0) return { lat: 12.9716, lng: 77.5946 }; // Default to Bengaluru
    return { lat: (start.lat + end.lat) / 2, lng: (start.lng + end.lng) / 2 };
  }, [start, end, path]);

  return (
    <Box>
      <PageShell
        title={technician?.name || 'Technician'}
        subtitle={
          technician
            ? [technician.phone, technician.area, technician.status].filter(Boolean).join(' · ')
            : 'Activity and location history'
        }
        action={
          technician && (
            <Badge colorScheme={technician.status === 'Active' ? 'green' : 'slate'} variant="subtle" px={3} py={1} borderRadius="full">
              {technician.technician_status || technician.technicianStatus || technician.status}
            </Badge>
          )
        }
      />

      <DataCard p={4} mb={4}>
      <HStack spacing={4} flexWrap="wrap" align="end">
        <Box>
          <Text fontSize="sm" fontWeight="600" color="slate.600" mb={1}>From</Text>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} borderRadius="lg" />
        </Box>
        <Box>
          <Text fontSize="sm" fontWeight="600" color="slate.600" mb={1}>To</Text>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} borderRadius="lg" />
        </Box>
        <Button colorScheme="brand" borderRadius="lg" onClick={fetchData} isLoading={loading}>
          Load Data
        </Button>
      </HStack>
      </DataCard>

      {/* Error Display */}
      {error && (
        <Alert status="error" mb={4}>
          <AlertIcon />
          <AlertTitle>Error!</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Flex justify="center" align="center" mb={4}>
          <Spinner size="lg" color="blue.500" />
          <Text ml={3}>Loading technician data...</Text>
        </Flex>
      )}

      <DataCard p={0} mb={4} overflow="hidden">
      <Box mb={0}>
        {apiKey ? (
          <LoadScript googleMapsApiKey={apiKey}>
            <GoogleMap mapContainerStyle={containerStyle} center={center} zoom={14}>
              {path.length > 0 && (
                <>
                  <Polyline
                    path={path}
                    options={{
                      strokeColor: '#FF0000',
                      strokeWeight: 4,
                      strokeOpacity: 0.8
                    }}
                  />
                  <Marker position={start} label="Start" />
                  <Marker position={end} label="End" />
                </>
              )}
              {path.length === 0 && (
                <Box position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)">
                  <Text fontSize="sm" color="gray.600">No location data available for selected date range</Text>
                </Box>
              )}
            </GoogleMap>
          </LoadScript>
        ) : (
          <Box p={4}>Set Google Maps API key in Utilities/Apikeys.js</Box>
        )}
      </Box>
      </DataCard>


      <DataCard p={4}>
      <VStack align="stretch" spacing={2}>
        <Text fontWeight="700" color="slate.800" mb={2}>Activity Logs</Text>
        {logs.length === 0 ? (
          <Text color="gray.500">No logs found for the selected date range</Text>
        ) : (
          logs.map((log, idx) => (
            <Box key={idx} p={3} borderWidth={1} borderRadius="md" borderColor="gray.200">
              <HStack justify="space-between">
                <VStack align="start" spacing={1}>
                  <Text fontWeight={600} color="blue.600">{log.action_type}</Text>
                  <Text>{log.action_description}</Text>
                  {log.old_value && log.new_value && (
                    <Text fontSize="sm" color="gray.600">
                      Changed from: {log.old_value} → {log.new_value}
                    </Text>
                  )}
                  {log.related_id && (
                    <Text fontSize="sm" color="gray.600">
                      Related ID: {log.related_id}
                    </Text>
                  )}
                  {(log.location_lat !== null && log.location_lng !== null && log.location_lat !== undefined && log.location_lng !== undefined) && (
                    <Text fontSize="sm" color="gray.600">
                      Location: {parseFloat(log.location_lat).toFixed(6)}, {parseFloat(log.location_lng).toFixed(6)}
                    </Text>
                  )}
                </VStack>
                <Text fontSize="xs" color="gray.500">
                  {formatLogTime(log.created_at)}
                </Text>
              </HStack>
            </Box>
          ))
        )}
      </VStack>
      </DataCard>
    </Box>
  );
}
